import { differenceInYears } from 'date-fns';
import { addIcons } from 'ionicons';
import { close } from 'ionicons/icons';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { LoadingService } from 'src/app/services/loading.service';
import { NotificationService } from 'src/app/services/notification/notification.service';

import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ApiService } from '@core/services';
import {
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonSegment,
  IonSegmentButton,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToolbar,
  ModalController,
} from '@ionic/angular/standalone';
import { Campus, User } from '@shared/models';
import { Supplier } from '@shared/models/supplier.model';

import { ApiResponse } from '../../../shared/models/api.model';

@Component({
  selector: 'app-register-saml-modal',
  imports: [
    IonHeader,
    IonToolbar,
    IonButtons,
    IonButton,
    IonIcon,
    IonContent,
    IonItem,
    IonLabel,
    IonCheckbox,
    IonSegment,
    IonSegmentButton,
    IonSelect,
    IonSelectOption,
    IonTitle,
    AsyncPipe,
    ReactiveFormsModule,
    IonFooter,
    CommonModule,
  ],
  templateUrl: './register-saml-modal.component.html',
  styleUrls: ['./register-saml-modal.component.css'],
})
export class RegisterSamlModalComponent implements OnInit {
  private apiService = inject(ApiService);
  private modalCtrl = inject(ModalController);
  private loadingService = inject(LoadingService);
  private notificationService = inject(NotificationService);
  private fb = inject(FormBuilder);

  @Input() user: Partial<User> | null = null;
  @Input() mode: 'register' | 'update' | 'read' = 'register';

  form: FormGroup = this.fb.group({
    campus: new FormControl<number | null>(null, { validators: Validators.required }),
    underAge: new FormControl<boolean>(false, { validators: Validators.requiredTrue }),
  });
  showUnderAgeFormControl = true;
  campus$: Observable<Campus[]> = this.getCampus();
  supplier$: Observable<Supplier | null> | undefined = this.form.get('campus')?.valueChanges.pipe(
    filter((campusId) => !!campusId),
    tap(() => this.loadingService.present()),
    switchMap((campusId) => this.getSupplier(campusId)),
    tap(() => this.loadingService.dismiss())
  );
  isLoading = signal(false);
  selectedTab = signal<'terms' | 'privacy' | 'student_regulations' | 'disciplinary_conduct'>('terms');

  constructor() {
    addIcons({ close });
  }

  ngOnInit(): void {
    setTimeout(() => {
      this.form.get('campus')?.setValue(this.user?.campus_current || null, { emitEvent: true });
      if (this.user?.birthday) {
        const birthday = new Date(this.user.birthday);
        const age = differenceInYears(new Date(), birthday);
        if (age >= 18) {
          this.form.get('underAge')?.setValue(true);
          this.showUnderAgeFormControl = false;
        }
      } else {
        this.form.get('underAge')?.setValue(true);
        this.showUnderAgeFormControl = false;
      }
    }, 0);
  }

  close() {
    this.modalCtrl.dismiss();
  }

  acceptTerms() {
    if (this.mode === 'register') {
      this.registerSamlUser();
    } else {
      this.updateTermsAndPrivacy();
    }
  }

  private registerSamlUser() {
    this.isLoading.set(true);
    this.loadingService.present();
    this.apiService
      .post('saml2/register', {
        ...this.user,
        campus_registered: this.form.get('campus')?.value,
        type: this.user?.role,
        terms: true,
      })
      .pipe(
        take(1),
        catchError(() => {
          this.notificationService.presentToast('Error al registrar usuario SAML. Favor de intentar nuevamente.');
          return of(null);
        })
      )
      .subscribe((response) => {
        if (response?.api_token && response?.user) {
          this.modalCtrl.dismiss(response);
        }
        this.isLoading.set(false);
        this.loadingService.dismiss();
      });
  }

  private updateTermsAndPrivacy() {
    this.isLoading.set(true);
    this.loadingService.present();
    this.apiService
      .put('profile/terms_privacy', {
        terms: 1,
        privacy: 1,
      })
      .pipe(
        take(1),
        catchError(() => {
          this.notificationService.presentToast('Favor de intentar nuevamente.');
          return of(null);
        })
      )
      .subscribe((response) => {
        console.log({ response });
        if (response) {
          this.modalCtrl.dismiss(response);
        }
        this.isLoading.set(false);
        this.loadingService.dismiss();
      });
  }

  private getCampus() {
    return this.apiService.get<ApiResponse<Campus[]>>('campus').pipe(
      map((res) => res.data),
      catchError(() => of([]))
    );
  }

  private getSupplier(campusId: number): Observable<Supplier | null> {
    return this.apiService.get<Supplier>(`supplier?campus_id=${campusId}`).pipe(catchError(() => of(null)));
  }
}
